import * as React from 'react';
import {
    CreateBase,
    useNotify,
    useRefresh,
    useDataProvider,
} from 'react-admin';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton as MuiIconButton,
    Box,
    Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { TextField as MuiTextField, MenuItem } from '@mui/material';
import { CommissionService } from "../../services/commission/commissionService.ts";

import { COMMISSIONS } from '../../enums/Commissions.ts';
import { useLocation, useNavigate } from "react-router-dom";
import { EstimatedTime } from "../../enums/EstimatedTime.ts";

type Role = keyof typeof COMMISSIONS;

const ESTIMATED_TIME_OPTIONS: { label: string; value: EstimatedTime }[] = [
    { label: '10 min', value: EstimatedTime.MIN_10 },
    { label: '15 min', value: EstimatedTime.MIN_15 },
    { label: '20 min', value: EstimatedTime.MIN_20 },
    { label: '25 min', value: EstimatedTime.MIN_25 },
    { label: '30 min', value: EstimatedTime.MIN_30 },
    { label: '40 min', value: EstimatedTime.MIN_40 },
    { label: '50 min', value: EstimatedTime.MIN_50 },
    { label: '60 min', value: EstimatedTime.MIN_60 },
    { label: '90 min', value: EstimatedTime.MIN_90 },
];

type Props = { open: boolean; onClose: () => void; onSelect: (time: EstimatedTime) => void; };

export const EstimatedTimeDialog: React.FC<Props> = ({ open, onClose, onSelect }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [items, setItems] = React.useState<
        { id: string; label: string; amount: number | string; unitType: string }[]
    >([]);

    const location = useLocation();
    const navigate = useNavigate();

    const initialValues = {
        name: '',
        role: '',
        amount: '',
        unitType: '',
    };

    const handleSelectTime = (time: EstimatedTime) => {
        onSelect(time);

        onClose();
    };


    const { t } = useTranslation();
    const commissionService = new CommissionService();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            slotProps={{
                backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.5)' } },
                paper: { sx: { borderRadius: 2, border: '1px solid var(--color-secondary)' } },
            }}
            sx={{ padding: 0 }}
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                <MuiIconButton aria-label="back" onClick={onClose} sx={{}}>
                    <ArrowBackIcon />
                </MuiIconButton>
                {t('orders.dialog.selectEstimatedTime')}
            </DialogTitle>

            <DialogContent>
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values, { resetForm, setSubmitting }) => {
                        const role = values.role as Role;
                        setItems(prev => [...prev, { id: role, label: COMMISSIONS[role].label, amount: values.amount, unitType: values.unitType }]);
                        notify('Item added', { type: 'success' });
                        resetForm({ values: { ...values, role: '', amount: '', unitType: '' } });
                        setSubmitting(false);
                    }}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                        <Form onSubmit={handleSubmit}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: 2,
                                    mt: 2,
                                }}
                            >
                                {ESTIMATED_TIME_OPTIONS.map(option => (
                                    <Button
                                        key={option.value}
                                        onClick={() => handleSelectTime(option.value)}
                                        sx={{
                                            borderRadius: 'var(--radius-12)',
                                            boxShadow: 'none',
                                            padding: 'var(--space-16)',
                                            backgroundColor: 'var(--color-secondary)',
                                            border: 'none',
                                            color: 'var(--color-text-primary)'
                                        }}
                                    >
                                        {option.label}
                                    </Button>
                                ))}
                            </Box>

                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};
